import { jwtDecode } from 'jwt-decode';

export const LIVE_OPENING_JITTER_MAX_MS = 5000;

const LIVE_STARTING_STREAM_STATUSES = new Set(['PENDING', 'PARTIAL', 'READY']);

function hash32(value: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  // FNV-1a mélange mal les bits faibles quand seul le dernier caractère change (ids consécutifs).
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;
  return hash >>> 0;
}

/** Identifiant du spectateur porté par son token, ou le token lui-même à défaut : stable d'un refresh à l'autre. */
export function liveOpeningJitterKey(token: string | null): string {
  if (!token) return '';
  try {
    const { attendeeId } = jwtDecode<{ attendeeId?: number | string }>(token);
    if (attendeeId !== undefined && attendeeId !== null && String(attendeeId).trim() !== '') {
      return String(attendeeId);
    }
  } catch {
    // token illisible : on se rabat sur sa valeur brute
  }
  return token;
}

/** Délai dans [0, maxMs[, identique pour un même couple spectateur/session afin de ne pas recréer de rafale au retry. */
export function liveOpeningJitterMs(
  attendeeKey: string,
  sessionIdentifier: string,
  maxMs: number = LIVE_OPENING_JITTER_MAX_MS,
): number {
  if (maxMs <= 0) return 0;
  return hash32(`${attendeeKey}:${sessionIdentifier}`) % Math.floor(maxMs);
}

/** Le présentateur vient de lancer le live : le SESSION_UPDATE porte alors un streamStartStatus non échoué. */
export function isLiveStartingUpdate(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const status = (data as { streamStartStatus?: unknown }).streamStartStatus;
  return typeof status === 'string' && LIVE_STARTING_STREAM_STATUSES.has(status);
}

/**
 * Session à annoncer comme « en cours de lancement » après un signal. Un statut de flux qui n'est pas un
 * lancement (FAILED, IDLE) annule l'annonce ; un signal sans statut, comme celui de l'admin, ne la change pas.
 */
export function nextLiveStartingSession(
  current: string | null,
  sessionIdentifier: string,
  data: unknown,
): string | null {
  if (isLiveStartingUpdate(data)) return sessionIdentifier;
  const status = (data as { streamStartStatus?: unknown } | null)?.streamStartStatus;
  return typeof status === 'string' ? null : current;
}

/** Signal de mise à jour de session : `live-event` côté admin, `event-message` côté back. */
export function isSessionUpdateMessage(name: string | undefined, data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  if (name === 'live-event') {
    return (data as { eventType?: unknown }).eventType === 'SESSION_UPDATED';
  }
  if (name === 'event-message') {
    return (data as { type?: unknown }).type === 'SESSION_UPDATE';
  }
  return false;
}

export interface SessionRefetchScheduler {
  signal: () => void;
  cancel: () => void;
}

/** `live` : session lancée, la page va basculer ; `waiting` : pas encore lancée ; `failed` : lecture en échec. */
export type SessionRefetchOutcome = 'live' | 'waiting' | 'failed';

/**
 * Un seul rechargement à la fois, du délai jusqu'à la réponse, et plus aucun une fois la session vue lancée.
 * Les signaux reçus pendant la requête donnent un seul rattrapage : immédiat si la session n'est pas encore
 * lancée (les réponses sont déjà étalées), après le délai du spectateur si la lecture a échoué.
 */
export function createSessionRefetchScheduler(
  delayMs: number,
  refetch: () => Promise<SessionRefetchOutcome>,
): SessionRefetchScheduler {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight = false;
  let signaledDuringFetch = false;
  let done = false;

  const schedule = () => {
    if (timer !== null) return;
    timer = setTimeout(() => {
      timer = null;
      void run();
    }, delayMs);
  };

  const run = async () => {
    inFlight = true;
    signaledDuringFetch = false;
    let outcome: SessionRefetchOutcome;
    try {
      outcome = await refetch();
    } catch {
      outcome = 'failed';
    }
    inFlight = false;
    if (done) return;
    if (outcome === 'live') {
      done = true;
      return;
    }
    if (!signaledDuringFetch) return;
    if (outcome === 'waiting') {
      void run();
    } else {
      schedule();
    }
  };

  return {
    signal() {
      if (done) return;
      if (inFlight) {
        signaledDuringFetch = true;
        return;
      }
      schedule();
    },
    cancel() {
      done = true;
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}
